import { useAppSelector } from '@app';
import { useApiQuery } from '@common/hooks';
import { useGetUserDataQuery } from '../api';
import {
    selectIsAuthenticated,
    selectLoginTime,
    selectRemainingSessionTime,
    selectSessionExpiryTime,
    selectToken,
    selectUserData,
    selectUserId,
} from '../slices';
import { DEFAULT_USER_DATA, GetUserDataArgs, UserData } from '../types';
import { useMemo } from 'react';
import { mergeWithDefault } from '@common/utils/mergeWithDefault';

export const useIsAuthenticated = () => {
    return useAppSelector(selectIsAuthenticated);
};

export const useUserId = () => {
    return useAppSelector(selectUserId);
};

export const useAuthToken = () => {
    return useAppSelector(selectToken);
};

export const useAuthentication = () => {
    const { isError, isFetching, isLoading, isSuccess } = useApiQuery<
        UserData,
        GetUserDataArgs
    >(useGetUserDataQuery, undefined);

    const rawUserData = useAppSelector(selectUserData);

    const isAuthenticated = useIsAuthenticated();
    const loginTime = useAppSelector(selectLoginTime);
    const remainingSessionTime = useAppSelector(selectRemainingSessionTime);
    const sessionExpiryTime = useAppSelector(selectSessionExpiryTime);

    const userData: UserData = useMemo(() => {
        return mergeWithDefault(rawUserData, DEFAULT_USER_DATA);
    }, [rawUserData]);

    return {
        ...userData,

        isAuthenticated,
        loginTime,
        remainingSessionTime,
        sessionExpiryTime,

        hasUserData: !!rawUserData,

        isError,
        isFetching,
        isLoading,
        isSuccess,

        user: userData,
    };
};
